// src/components/pages/CreateTicketForm.jsx

import React, { useEffect, useState } from 'react';
import Navbar from '../../../Layout/Navbar/Navbar';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { EventGetItems } from '../../../../redux/createEvent/createEventAction/EventDetailAction';
import { createTicket } from '../../../../redux/CreateTicket/createTicketAction/createTicketAction';
import { Field, Form, Formik } from 'formik';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Footer from '../../../Layout/Footer/Footer';
import Shimmer from '../../../Layout/Shimmer';
import ReactGA from 'react-ga4';
import * as Yup from 'yup';

function CreateTicketForm() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Redux State Selectors
  const eventItem = useSelector((state) => state.eventItems);
  const { data, loading, error } = eventItem;

  // Local State
  const [ticketPrice, setTicketPrice] = useState(data?.ticket_price);
  const [totalQuantity, setTotalQuantity] = useState(data?.capacity);
  const [hasSubmitted, setHasSubmitted] = useState(false);

  // Ticket category dropdown options
  const [ticketCategories] = useState([
    { value: 'General', label: 'General' },
    { value: 'Free', label: 'Free' },
    { value: 'Early Bird', label: 'Early Bird' },
    { value: 'Vip', label: 'VIP/Premium' },
    { value: 'Front Row', label: 'Front Row' },
    { value: 'Member', label: 'Member' },
    { value: 'Group', label: 'Group' },
    { value: 'Gold', label: 'Gold' },
    { value: 'Silver', label: 'Silver' },
    { value: 'Bronze', label: 'Bronze' },
    { value: 'Student', label: 'Student' },
    { value: 'Senior', label: 'Senior' },
    { value: 'custom', label: 'Custom' }
  ]);

  const [customCategoryName, setCustomCategoryName] = useState('');

  // Fetch event data on mount
  useEffect(() => {
    if (id) {
      dispatch(EventGetItems(id));
    }
  }, [dispatch, id]);

  // Update local states when event data changes
  useEffect(() => {
    if (data) {
      setTicketPrice(data.ticket_price);
      setTotalQuantity(data.capacity);
    }
  }, [data]);

  // Calculate how many seats are still available for the whole event
  const calculateAvailableTickets = () => {
    /* If the backend already supplies a `remaining_capacity` or
       `available_tickets` field, use that authoritative value. */
    if (data?.remaining_capacity !== undefined && data?.remaining_capacity !== null) {
      return data.remaining_capacity;
    }
    if (data?.available_tickets !== undefined && data?.available_tickets !== null) {
      return data.available_tickets;
    }

    /* Otherwise, compute remaining seats by subtracting the TOTAL tickets
       that have been *allocated* across all categories (regardless of how
       many have been sold) from the event’s overall capacity.  This mirrors
       the backend validation that rejects over‑allocation. */
    if (Array.isArray(data?.tickets)) {
      const allocated = data.tickets.reduce((sum, ticket) => {
        const total = parseInt(ticket.total_quantity, 10) || 0;
        return sum + total;
      }, 0);

      return Math.max(0, (data.capacity || 0) - allocated);
    }

    // Fallback when no information is available
    return data?.capacity || 0;
  };

  // Get the available tickets count
  const availableTickets = calculateAvailableTickets();

  // Define Yup validation schema (handles 'custom' vs. non-custom)
  const validationSchema = Yup.lazy((values) => {
    if (values.ticket_category === 'custom') {
      return Yup.object().shape({
        event_name: Yup.string().required('Event Name is required'),
        ticket_price: Yup.number()
          .typeError('Ticket Price must be a number')
          .required('Ticket Price is required'),
        total_quantity: Yup.number()
          .typeError('Total Quantity must be a number')
          .integer('Total Quantity must be an integer')
          .min(1, 'Total Quantity must be at least 1')
          .max(availableTickets, `Maximum tickets cannot exceed available capacity (${availableTickets})`)
          .required('Total Quantity is required'),
        ticket_category: Yup.string().required('Ticket Category is required'),
        custom_category: Yup.string().required('Custom Category is required'),
        ticket_description: Yup.string().required('Ticket Description is required'),
      });
    } else {
      return Yup.object().shape({
        event_name: Yup.string().required('Event Name is required'),
        ticket_price: Yup.number()
          .typeError('Ticket Price must be a number')
          .required('Ticket Price is required'),
        total_quantity: Yup.number()
          .typeError('Total Quantity must be a number')
          .integer('Total Quantity must be an integer')
          .min(1, 'Total Quantity must be at least 1')
          .max(availableTickets, `Maximum tickets cannot exceed available capacity (${availableTickets})`)
          .required('Total Quantity is required'),
        ticket_category: Yup.string().required('Ticket Category is required'),
        ticket_description: Yup.string().required('Ticket Description is required'),
      });
    }
  });

  // Handle form submission
  const handleSubmit = (values, { resetForm }) => {
    const formData = new FormData();
    formData.append('event', id);
    formData.append('event_name', values.event_name);
    formData.append('price', values.ticket_price);
    formData.append('total_quantity', values.total_quantity);
    
    // Automatically set available_quantity equal to total_quantity for new tickets
    formData.append('available_quantity', values.total_quantity);
    
    // Default ticket_sold to 0 for new tickets
    formData.append('ticket_sold', '0');

    const categoryName =
      values.ticket_category === 'custom'
        ? values.custom_category
        : ticketCategories.find(cat => cat.value === values.ticket_category)?.label;

    // If using custom category
    if (values.ticket_category === 'custom') {
      formData.append('custom_category', values.custom_category);
    } else {
      formData.append('ticket_category', values.ticket_category);
    }
    formData.append('ticket_description', values.ticket_description);

    dispatch(createTicket(formData))
      .then((response) => {
        // 1) If the backend returns an error under "response.error" or "response.payload.error"...
        if (response?.error) {
          toast.error(response.error, {
            position: 'top-right',
            autoClose: 3000,
          });
          return;
        }

        // 2) (OPTIONAL) If your thunk puts error in response.payload.error:
        if (response?.payload?.error) {
          toast.error(response.payload.error, {
            position: 'top-right',
            autoClose: 3000,
          });
          return;
        }

        // 3) If no error, show success toast
        toast.success(`Successfully created ${categoryName} ticket`, {
          position: 'top-right',
          autoClose: 3000,
        });

        ReactGA.event({
          category: 'Ticket',
          action: 'Create Ticket',
          label: categoryName,
        });

        resetForm();
        setHasSubmitted(true);
      })
      .catch((error) => {
        console.error(error);
        toast.error('Error creating ticket', {
          position: 'top-right',
          autoClose: 3000,
        });
      });
  };

  // ========== RENDERING LOGIC ==========

  if (loading) {
    return (
      <div className="w-full h-auto font-poppins text-[#565656] bg-[#f2f4f7]">
        <Navbar linkTo="/home" title="Create Ticket" />
        <div className="max-w-lg mx-auto space-y-4 bg-white shadow-md rounded-lg my-10">
          <Shimmer width="536px" height="711px" />
        </div>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full h-auto font-poppins text-[#565656] bg-[#f2f4f7]">
        <Navbar linkTo="/home" title="Create Ticket" />
        <div className="max-w-lg mx-auto p-6 bg-white shadow-md rounded-lg my-10">
          <p className="text-red-500">{error}</p>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="w-full h-auto font-poppins text-[#565656] bg-[#f2f4f7]">
      <Navbar linkTo="/home" title="Create Ticket" />
      
      <div className="max-w-lg mx-auto p-6 space-y-4 bg-white shadow-md rounded-lg my-10">
        {/* Event capacity banner - matches screenshot format */}
        <div className="bg-blue-50 p-4 rounded-lg mb-6">
          <div className="flex flex-col">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-medium">Event: {data?.event_name}</h3>
              <span className="bg-[#004B95] text-white px-4 py-2 rounded-lg text-sm font-medium">
                Total Capacity: {data?.capacity || 0}
              </span>
            </div>
            
            {/* Available tickets section */}
            <div className="mt-4">
              <div className="flex justify-between items-center mb-2">
                <span className="font-medium">Available Tickets</span>
                <span className="font-bold text-[#004B95]">
                  {availableTickets} remaining
                </span>
              </div>
              
              {/* Progress bar */}
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div 
                  className="bg-[#004B95] h-2.5 rounded-full" 
                  style={{ 
                    width: `${Math.min(100, Math.round((availableTickets / (data?.capacity || 1)) * 100))}%` 
                  }}
                ></div>
              </div>
              <p className="text-sm text-gray-700 mt-2">
                You can create up to {availableTickets} more tickets for this event.
              </p>
            </div>
          </div>
        </div>
      
        <Formik
          enableReinitialize
          initialValues={{
            event_name: data?.event_name || '',
            ticket_price: ticketPrice || '',
            total_quantity: '',
            ticket_category: '',
            custom_category: '',
            ticket_description: '',
          }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            setFieldValue
          }) => (
            <Form className="flex flex-col">
              {/* Price & Category in same row */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                {/* Ticket Price */}
                <div>
                  <p className="font-medium font-poppins text-base tracking-wider">
                    Ticket Price<span className="text-red-500">*</span>
                  </p>
                  <Field
                    id="ticket_price"
                    name="ticket_price"
                    type="number"
                    min="0"
                    placeholder="Ticket Price"
                    className={`w-full text-sm p-3 rounded-lg pl-4 font-poppins tracking-wide border ${
                      errors.ticket_price && touched.ticket_price
                        ? 'border-red-500'
                        : 'border-sunday_secondary'
                    }`}
                    onChange={(e) => {
                      handleChange(e);
                      const price = parseFloat(e.target.value);
                      // Example: if price is 0, set category to 'Free'.
                      if (!isNaN(price)) {
                        if (price === 0) {
                          setFieldValue('ticket_category', 'Free');
                        } else if (price > 0 && values.ticket_category === 'Free') {
                          setFieldValue('ticket_category', '');
                        }
                      }
                    }}
                  />
                  {errors.ticket_price && touched.ticket_price && (
                    <div className="text-red-500 text-sm mt-1">
                      {errors.ticket_price}
                    </div>
                  )}
                </div>

                {/* Ticket Category */}
                <div>
                  <p className="font-medium font-poppins text-base tracking-wider">
                    Ticket Category<span className="text-red-500">*</span>
                  </p>
                  <Field
                    name="ticket_category"
                    as="select"
                    className={`w-full text-sm p-3 rounded-lg pl-4 font-poppins tracking-wide border ${
                      errors.ticket_category && touched.ticket_category
                        ? 'border-red-500'
                        : 'border-sunday_secondary'
                    }`}
                    onChange={(e) => {
                      handleChange(e);
                      // Reset custom category name when switching out of custom
                      if (e.target.value !== 'custom') {
                        setCustomCategoryName('');
                        setFieldValue('custom_category', '');
                      }
                    }}
                  >
                    <option value="" disabled hidden>
                      Select category
                    </option>
                    {ticketCategories.map(category => (
                      <option
                        key={category.value}
                        value={category.value}
                        disabled={category.value === 'Free' && parseFloat(values.ticket_price) > 0}
                      >
                        {category.label}
                      </option>
                    ))}
                  </Field>
                  {errors.ticket_category && touched.ticket_category && (
                    <div className="text-red-500 text-sm mt-1">
                      {errors.ticket_category}
                    </div>
                  )}
                </div>
              </div>

              {/* Custom Category Input - shown only if 'custom' is selected */}
              {values.ticket_category === 'custom' && (
                <div className="mb-4">
                  <p className="font-medium font-poppins text-base tracking-wider">
                    Custom Category Name<span className="text-red-500">*</span>
                  </p>
                  <Field
                    name="custom_category"
                    type="text"
                    placeholder="Enter custom category name"
                    className={`w-full text-sm p-3 rounded-lg pl-4 font-poppins tracking-wide border ${
                      errors.custom_category && touched.custom_category
                        ? 'border-red-500'
                        : 'border-sunday_secondary'
                    }`}
                    onChange={(e) => {
                      handleChange(e);
                      setCustomCategoryName(e.target.value);
                    }}
                  />
                  {errors.custom_category && touched.custom_category && (
                    <div className="text-red-500 text-sm mt-1">
                      {errors.custom_category}
                    </div>
                  )}
                </div>
              )}

              {/* Total Quantity with remaining count indicator */}
              <div className="mb-4">
                <p className="font-medium">
                  Total Tickets<span className="text-red-500">*</span>
                </p>
                <div className="relative">
                  <Field
                    id="total_quantity"
                    name="total_quantity"
                    type="number"
                    min="1"
                    max={availableTickets}
                    placeholder="Enter ticket quantity"
                    className={`w-full text-sm p-3 rounded-lg pl-4 border ${
                      errors.total_quantity && touched.total_quantity
                        ? 'border-red-500'
                        : 'border-sunday_secondary'
                    }`}
                  />
                  {values.total_quantity && (
                    <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-sm text-gray-500">
                      {values.total_quantity} of {availableTickets} total
                    </span>
                  )}
                </div>
                {errors.total_quantity && touched.total_quantity && (
                  <div className="text-red-500 text-sm mt-1">
                    {errors.total_quantity}
                  </div>
                )}
              </div>

              {/* Warning when tickets would exceed capacity */}
              {values.total_quantity > availableTickets && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                  <div className="text-red-500 mt-1">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-medium text-red-700">Total Quantity: Total tickets would exceed event capacity.</p>
                    <p className="text-red-600">Remaining capacity: {availableTickets}</p>
                  </div>
                </div>
              )}

              {/* Ticket Description */}
              <div className="mb-4">
                <p className="font-medium font-poppins text-base tracking-wider">
                  Ticket Description<span className="text-red-500">*</span>
                </p>
                <Field
                  id="ticket_description"
                  name="ticket_description"
                  as="textarea"
                  rows="4"
                  placeholder="Describe what this ticket includes, any restrictions, or special instructions"
                  className={`w-full text-sm p-3 rounded-lg pl-4 font-poppins tracking-wide border ${
                    errors.ticket_description && touched.ticket_description
                      ? 'border-red-500'
                      : 'border-sunday_secondary'
                  }`}
                />
                {errors.ticket_description && touched.ticket_description && (
                  <div className="text-red-500 text-sm mt-1">
                    {errors.ticket_description}
                  </div>
                )}
              </div>

              {/* Quick summary of chosen category */}
              {(values.ticket_category || customCategoryName) && (
                <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                  <p className="font-medium">Selected Category:</p>
                  <p className="text-gray-600">
                    {values.ticket_category === 'custom'
                      ? customCategoryName || 'Custom Category'
                      : ticketCategories.find(cat => cat.value === values.ticket_category)?.label
                    }
                  </p>
                </div>
              )}

              {/* Submit Button */}
              <div className="flex justify-center items-center mb-4 mt-6">
                <button
                  type="submit"
                  disabled={hasSubmitted}
                  className={`bg-[#004B95] text-white rounded-lg w-full p-3 font-poppins text-base tracking-wide transition duration-150
                    ${
                      hasSubmitted
                        ? 'cursor-not-allowed opacity-70 hover:opacity-70'
                        : 'cursor-pointer hover:brightness-95'
                    }`
                  }
                >
                  Create Ticket
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
      <Footer />
    </div>
  );
}

export default CreateTicketForm;